import React, { useState, useEffect, useRef } from "react";
import "./TrainingPlanPane.css";
import { updateProfessionalContent } from "../../../../backend/firebaseFunctions";
import "bootstrap/dist/css/bootstrap.min.css";
import Joyride, { STATUS } from "react-joyride";
import { FaQuestionCircle } from "react-icons/fa";
import { Alert } from "react-bootstrap";

const MONTHS = {
  1: "ינואר",
  2: "פברואר",
  3: "מרץ",
  4: "אפריל",
  5: "מאי",
  6: "יוני",
  7: "יולי",
  8: "אוגוסט",
  9: "ספטמבר",
  10: "אוקטובר",
  11: "נובמבר",
  12: "דצמבר",
};

// Spinner component for loading states
const LoadingSpinner = () => <div className="spinner"></div>;

// Add these utility functions at the top
const DRAFT_KEY_PREFIX = "training_plan_draft_";
const LAST_EDIT_KEY = "training_plan_last_edit";

const saveDraft = (key, data) => {
  try {
    localStorage.setItem(DRAFT_KEY_PREFIX + key, JSON.stringify(data));
    localStorage.setItem(LAST_EDIT_KEY, key);
  } catch (e) {
    console.warn("Failed to save draft:", e);
  }
};

const getDraft = (key) => {
  try {
    const draft = localStorage.getItem(DRAFT_KEY_PREFIX + key);
    return draft ? JSON.parse(draft) : null;
  } catch (e) {
    console.warn("Failed to get draft:", e);
    return null;
  }
};

const clearDraft = (key) => {
  try {
    localStorage.removeItem(DRAFT_KEY_PREFIX + key);
    localStorage.removeItem(LAST_EDIT_KEY);
  } catch (e) {
    console.warn("Failed to clear draft:", e);
  }
};

// Update the EditForm component
const EditForm = ({
  initialData,
  onSave,
  onCancel,
  isSaving,
  saveError,
  editKey,
}) => {
  const defaultData = {
    title: "",
    description: "",
  };

  // Check for existing draft when component mounts
  const existingDraft = getDraft(editKey);

  const [formData, setFormData] = useState({
    ...defaultData,
    ...initialData,
    ...(existingDraft || {}), // Load draft if exists
  });

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Auto-save draft when form data changes
  useEffect(() => {
    if (hasUnsavedChanges) {
      saveDraft(editKey, formData);
    }
  }, [formData, editKey, hasUnsavedChanges]);

  // Warn before closing/refreshing if there are unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setHasUnsavedChanges(true);
  };

  const handleSave = async () => {
    await onSave(formData);
    setHasUnsavedChanges(false);
    clearDraft(editKey);
  };

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      if (
        window.confirm("יש לך שינויים שלא נשמרו. האם אתה בטוח שברצונך לבטל?")
      ) {
        clearDraft(editKey);
        onCancel();
      }
    } else {
      onCancel();
    }
  };

  return (
    <div className="edit-form">
      {hasUnsavedChanges && (
        <div className="draft-indicator">
          <span className="draft-icon">📝</span>
          טיוטה נשמרה
        </div>
      )}
      <input
        type="text"
        value={formData.title}
        onChange={(e) => handleInputChange("title", e.target.value)}
        placeholder="כותרת"
        className="edit-input"
        disabled={isSaving}
        lang="he"
        dir="rtl"
      />
      <textarea
        value={formData.description}
        onChange={(e) => handleInputChange("description", e.target.value)}
        placeholder="תיאור"
        className="edit-textarea"
        disabled={isSaving}
        lang="he"
        dir="rtl"
      />
      {saveError && (
        <div className="error-message">
          <span className="error-icon">⚠️</span>
          {saveError}
        </div>
      )}
      <div className="edit-actions">
        <button
          onClick={handleSave}
          className={`save-button ${isSaving ? "saving" : ""}`}
          disabled={isSaving}
        >
          {isSaving ? (
            <>
              <span
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <LoadingSpinner />
                שומר...
              </span>
            </>
          ) : (
            "שמור"
          )}
        </button>
        <button
          onClick={handleCancel}
          className="cancel-button"
          disabled={isSaving}
        >
          ביטול
        </button>
      </div>
    </div>
  );
};

function TrainingPlanPane({
  userData,
  professionalContent,
  userID,
}) {
  const [editingMonth, setEditingMonth] = useState(null); // Format: "year-month"
  const [editingWeek, setEditingWeek] = useState(null); // Format: "year-month-week"
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [isYearDropdownOpen, setIsYearDropdownOpen] = useState(false);
  const [selectedYear, setSelectedYear] = useState(
    new Date().getFullYear().toString()
  );
  const [runTour, setRunTour] = useState(false);

  const hasViewPrivilege =
    userData?.userType === "manager" ||
    userData?.coachData?.accessPrivilege?.trainingPlan?.view ||
    (userData?.userType === "coach" && !userData?.coachData) || // Default allow if coachData is missing
    false;
  const hasEditPrivilege =
    userData?.userType === "manager" ||
    userData?.coachData?.accessPrivilege?.trainingPlan?.write ||
    (userData?.userType === "coach" && !userData?.coachData) || // Default allow if coachData is missing
    false;


  // Check for any unsaved drafts when component mounts
  useEffect(() => {
    const lastEditKey = localStorage.getItem(LAST_EDIT_KEY);
    if (lastEditKey) {
      const draft = getDraft(lastEditKey);
      if (draft) {
        const [type, ...parts] = lastEditKey.split("-");
        if (type === "month") {
          setEditingMonth(parts.join("-"));
        } else if (type === "week") {
          setEditingWeek(parts.join("-"));
        }
      }
    }
  }, []);

  const handleMonthEdit = (year, month) => {
    setEditingMonth(`${year}-${month}`);
    setEditingWeek(null);
  };

  const handleWeekEdit = (year, month, week) => {
    setEditingWeek(`${year}-${month}-${week}`);
    setEditingMonth(null);
  };

  // Updated isEmptyData function with better null checking
  const isEmptyData = (data) => {
    if (!data) return true;
    if (typeof data !== "object") return true;

    const title = data.title || "";
    const description = data.description || "";

    return title.trim() === "" && description.trim() === "";
  };

  const handleSaveEdit = async (formData) => {
    setIsSaving(true);
    setSaveError(null);
    try {
      // Create updated content structure based on current professionalContent
      const currentContent = professionalContent || { years: { "0": {} } };

      const updatedContent = {
        ...currentContent,
        years: {
          ...currentContent.years,
          "0": {
            ...currentContent.years["0"],
            [selectedYear]: {
              ...currentContent.years["0"][selectedYear],
              // Update month or week based on what's being edited
              ...(editingMonth
                ? {
                  [editingMonth.split("-")[1]]: {
                    ...currentContent.years["0"][selectedYear]?.[
                    editingMonth.split("-")[1]
                    ],
                    ...formData,
                  },
                }
                : {}),
              ...(editingWeek
                ? {
                  [editingWeek.split("-")[1]]: {
                    ...currentContent.years["0"][selectedYear]?.[
                    editingWeek.split("-")[1]
                    ],
                    weeks: {
                      ...currentContent.years["0"][selectedYear]?.[
                        editingWeek.split("-")[1]
                      ]?.weeks,
                      [editingWeek.split("-")[2]]: formData,
                    },
                  },
                }
                : {}),
            },
          },
        },
      };

      // Update Firebase - this will trigger the real-time listener
      await updateProfessionalContent(getTargetUserID(), updatedContent);

      setIsSaving(false);

      // Clear editing state
      setEditingMonth(null);
      setEditingWeek(null);
    } catch (error) {
      setSaveError("Failed to save changes. Please try again.");
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditingMonth(null);
    setEditingWeek(null);
  };

  // Helper to get the correct UID for training plan actions
  const getTargetUserID = () => {
    if (
      userData &&
      userData.userType === "coach" &&
      userData.userOrganization?.organizationOwnerID
    ) {
      return userData.userOrganization.organizationOwnerID;
    }
    return userID;
  };

  // --- DESIGN STYLES ---
  const mainBg = {
    minHeight: "100vh",
    background: "linear-gradient(135deg, #f8fafc 0%, #e3e9f7 100%)",
    padding: "32px 0",
  };
  const containerStyle = {
    maxWidth: 1200,
    margin: "0 auto",
    padding: "0 16px",
  };
  // --- END DESIGN STYLES ---

  const renderWeeks = (year, monthNum, monthData) => {
    const weeks = [];
    const currentMonthData = professionalContent?.years?.["0"]?.[year]?.[monthNum] || {};

    for (let i = 1; i <= 4; i++) {
      const weekData = currentMonthData?.weeks?.[i];
      const isEditing = editingWeek === `${year}-${monthNum}-${i}`;
      const isEmpty = isEmptyData(weekData);

      weeks.push(
        <div
          key={`${year}-${monthNum}-${i}`}
          className={`modern-week-card week-card ${isEmpty ? "empty-week" : ""
            } ${isEditing ? "editing" : ""}`}
        >
          <div className="week-content">
            <div className="week-header">
              <span className="week-number">שבוע {i}</span>
              {!isEditing ? (
                <>
                  {!isEmpty && weekData ? (
                    <>
                      <h4 className="modern-week-title week-title">
                        {weekData.title || ""}
                      </h4>
                      {weekData.description &&
                        weekData.description.trim() !== "" && (
                          <p className="modern-week-description week-description">
                            {weekData.description}
                          </p>
                        )}
                    </>
                  ) : (
                    <span className="modern-empty-message empty-week-message">
                      לא הוגדר תוכן לשבוע זה
                    </span>
                  )}
                </>
              ) : (
                <EditForm
                  initialData={weekData || { title: "", description: "" }}
                  onSave={handleSaveEdit}
                  onCancel={handleCancel}
                  isSaving={isSaving}
                  saveError={saveError}
                  editKey={`week-${year}-${monthNum}-${i}`}
                />
              )}
            </div>
          </div>
          {!isEditing && hasEditPrivilege && (
            <button
              className="modern-edit-btn edit-button"
              onClick={() => handleWeekEdit(year, monthNum, i)}
              title="ערוך שבוע"
            >
              <span className="edit-icon">✎</span>
            </button>
          )}
        </div>
      );
    }
    return weeks;
  };

  const renderMonths = (year, yearData) => {
    const months = [];
    const currentYearData = professionalContent?.years?.["0"]?.[year] || {};

    for (let i = 1; i <= 12; i++) {
      const monthData = currentYearData[i];
      const isEditing = editingMonth === `${year}-${i}`;
      const isEmpty = isEmptyData(monthData);

      months.push(
        <div
          key={`${year}-${i}`}
          className={`modern-month-card month-card ${isEmpty ? "empty-month" : ""
            } ${isEditing ? "editing" : ""}`}
        >
          <div className="month-header">
            <div className="month-title-wrapper">
              <h3 className="modern-month-name month-name">{MONTHS[i]}</h3>
              {!isEditing ? (
                <div className="month-title-content">
                  {!isEmpty && monthData ? (
                    <>
                      <span className="month-subtitle">
                        {monthData.title || ""}
                      </span>
                      {monthData.description &&
                        monthData.description.trim() !== "" && (
                          <p className="modern-month-description month-description">
                            {monthData.description}
                          </p>
                        )}
                    </>
                  ) : (
                    <span className="modern-empty-message empty-month-message">
                      לא הוגדר תוכן לחודש זה
                    </span>
                  )}
                  {hasEditPrivilege && (
                    <button
                      className="modern-edit-btn edit-button"
                      onClick={() => handleMonthEdit(year, i)}
                      title="ערוך חודש"
                    >
                      <span className="edit-icon">✎</span>
                    </button>
                  )}
                </div>
              ) : (
                <EditForm
                  initialData={monthData || { title: "", description: "" }}
                  onSave={handleSaveEdit}
                  onCancel={handleCancel}
                  isSaving={isSaving}
                  saveError={saveError}
                  editKey={`month-${year}-${i}`}
                />
              )}
            </div>
          </div>
          <div className="modern-weeks-container weeks-container">
            {renderWeeks(year, i, monthData)}
          </div>
        </div>
      );
    }
    return months;
  };

  // Get a range of years
  const getAvailableYears = () => {
    const startYear = 2020;
    const endYear = 2030;
    return Array.from({ length: endYear - startYear + 1 }, (_, i) =>
      (startYear + i).toString()
    );
  };

  const YearDropdown = ({ year }) => {
    const dropdownRef = useRef(null);

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target)
        ) {
          setIsYearDropdownOpen(false);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    return (
      <div
        className="year-dropdown-container"
        style={{ backgroundColor: "transparent" }}
        ref={dropdownRef}
      >
        <button
          className={`btn btn-light year-button ${isYearDropdownOpen ? "show" : ""
            }`}
          onClick={() => setIsYearDropdownOpen(!isYearDropdownOpen)}
          aria-expanded={isYearDropdownOpen}
          style={{
            backgroundColor: "white",
            padding: "8px 16px",
            borderRadius: "6px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <span
            className="year-display"
            style={{ fontSize: "1.5rem", fontWeight: "bold" }}
          >
            {year}
          </span>
          <svg
            className={`year-dropdown-arrow ${isYearDropdownOpen ? "open" : ""
              }`}
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ marginLeft: "4px" }}
          >
            <path
              d="M7 10L12 15L17 10"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <div
          className={`dropdown-menu year-dropdown-menu ${isYearDropdownOpen ? "show" : ""
            }`}
        >
          {getAvailableYears().map((yearOption) => (
            <button
              key={yearOption}
              className={`dropdown-item year-dropdown-item ${yearOption === year ? "active" : ""
                }`}
              onClick={() => {
                setSelectedYear(yearOption);
                setIsYearDropdownOpen(false);
              }}
            >
              {yearOption}
            </button>
          ))}
        </div>
      </div>
    );
  };

  // Tour button style
  const tourButtonStyle = {
    position: "absolute",
    top: "20px",
    left: "20px",
    backgroundColor: "#0066CC",
    color: "white",
    border: "none",
    borderRadius: "50%",
    width: "40px",
    height: "40px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transition: "all 0.3s ease",
    zIndex: 100,
    boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
  };

  // Define tour steps
  const steps = [
    {
      target: ".training-plan-wrapper",
      content:
        "ברוכים הבאים למסך תכנית האימונים! כאן תוכלו לנהל את תכנית האימונים השנתית, ולהוסיף תוכן מקצועי לכל חודש ושבוע. בואו נעשה סיור קצר!",
      placement: "center",
      disableBeacon: true,
    },
    {
      target: ".year-dropdown-container",
      content:
        "כאן תוכלו לבחור את השנה שברצונכם לצפות בה. התכנית מאפשרת ניהול של מספר שנים קדימה.",
      placement: "bottom",
    },
    {
      target: ".months-grid",
      content:
        "כאן תוכלו לראות את כל החודשים בשנה. כל חודש מכיל 4 שבועות כאשר לכל חודש ושבוע ניתן להוסיף תוכן.",
      placement: "bottom",
    },
    {
      target: ".week-card",
      content:
        "לחצו על שבוע כדי לערוך את התוכן הספציפי שיועבר בו. תוכלו להוסיף כותרת ותיאור לכל שבוע.",
      placement: "right",
    },
    {
      target: ".training-guide-button",
      content: "אם תרצו לראות את הסיור שוב, תוכלו ללחוץ על כפתור זה בכל עת!",
      placement: "left",
      spotlightClicks: true,
    },
  ];

  // Handle tour events
  const handleJoyrideCallback = (data) => {
    const { status } = data;
    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      setRunTour(false);
    }
  };

  const handleTourButtonClick = () => {
    setRunTour(true);
  };

  // If user doesn't have view privilege, show message and return
  if (!hasViewPrivilege) {
    return (
      <div style={mainBg}>
        <div style={containerStyle}>
          <div className="training-plan-wrapper">
            <Alert
              variant="warning"
              className="m-3"
              style={{ direction: "rtl" }}
            >
              אין לך הרשאות לצפייה בתכנית האימונים. אנא פנה למנהל המערכת לקבלת
              הרשאות.
            </Alert>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={mainBg}>
      <style>{`
        .spinner {
          width: 16px;
          height: 16px;
          border: 2px solid #ffffff;
          border-radius: 50%;
          border-top-color: transparent;
          animation: spin 1s linear infinite;
          margin-left: 8px;
          display: inline-block;
          vertical-align: middle;
        }

        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }

        .save-button {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          min-width: 100px;
        }

        .save-button.saving {
          background-color: #0052a3;
          cursor: not-allowed;
        }

        .error-message {
          background-color: #fee2e2;
          border: 1px solid #fecaca;
          border-radius: 4px;
          padding: 8px 12px;
          margin: 8px 0;
          display: flex;
          align-items: center;
          gap: 8px;
          color: #dc2626;
        }

        .error-icon {
          font-size: 16px;
        }

        .success-message {
          background-color: #dcfce7;
          border: 1px solid #bbf7d0;
          border-radius: 4px;
          padding: 8px 12px;
          margin: 8px 0;
          display: flex;
          align-items: center;
          gap: 8px;
          color: #16a34a;
        }

        .edit-input:disabled,
        .edit-textarea:disabled {
          background-color: #f1f5f9;
          cursor: not-allowed;
          opacity: 0.7;
        }

        .cancel-button:disabled {
          background-color: #e2e8f0;
          cursor: not-allowed;
          opacity: 0.7;
        }

        .edit-form {
          position: relative;
        }

        .edit-form::after {
          content: '';
          position: absolute;
          inset: 0;
          background-color: rgba(255, 255, 255, 0.5);
          pointer-events: none;
          opacity: 0;
          transition: opacity 0.2s;
        }

        .edit-form.saving::after {
          opacity: 1;
          pointer-events: all;
        }

        .draft-indicator {
          background-color: #f0f9ff;
          border: 1px solid #bae6fd;
          border-radius: 4px;
          padding: 4px 8px;
          margin-bottom: 8px;
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 0.875rem;
          color: #0369a1;
        }

        .draft-icon {
          font-size: 1rem;
        }

        .year-dropdown-container {
          position: relative;
          display: inline-block;
        }

        .year-button {
          transition: background-color 0.2s ease !important;
        }

        .year-button:hover {
          background-color: #f8f9fa ;
        }

        .year-dropdown-arrow {
          transition: transform 0.2s ease;
        }

        .year-dropdown-arrow.open {
          transform: rotate(180deg);
        }

        .dropdown-menu.year-dropdown-menu {
          min-width: 180px !important;
          max-height: 300px !important;
          overflow-y: auto !important;
          padding: 0.5rem 0 !important;
          margin-top: 0.25rem !important;
          border: 1px solid rgba(0, 0, 0, 0.1) !important;
          border-radius: 8px !important;
          box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
          background: white !important;
        }

        .dropdown-menu.year-dropdown-menu.show {
          display: block;
          animation: dropdownFade 0.2s ease;
        }

        .dropdown-item.year-dropdown-item {
          padding: 0.75rem 1.25rem !important;
          font-size: 1.5rem !important;
          color: #495057 !important;
          text-align: center !important;
          transition: all 0.15s ease !important;
          background: transparent !important;
          cursor: pointer !important;
          font-weight: 500 !important;
        }

        .dropdown-item.year-dropdown-item:hover,
        .dropdown-item.year-dropdown-item:focus,
        .dropdown-item.year-dropdown-item:active {
          background-color: #f8f9fa !important;
          color: #1a202c !important;
        }

        .dropdown-item.year-dropdown-item.active {
          background-color: #e9ecef !important;
          color: #2b6cb0 !important;
          font-weight: 600 !important;
        }

        @keyframes dropdownFade {
          from {
            opacity: 0;
            transform: translateY(-4px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* Custom scrollbar for the dropdown */
        .year-dropdown-menu::-webkit-scrollbar {
          width: 6px;
        }

        .year-dropdown-menu::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 3px;
        }

        .year-dropdown-menu::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 3px;
        }

        .year-dropdown-menu::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }

        /* Override Bootstrap styles with more specific selectors */
        button.btn.btn-light.year-dropdown-button {
          display: flex !important;
          align-items: center !important;
          gap: 12px !important;
          font-size: 3rem !important;
          font-weight: 600 !important;
          padding: 0 !important;
          border: none !important;
          background: transparent !important;
          min-width: 200px !important;
          color: #1a202c !important;
          box-shadow: none !important;
          text-align: start !important;
        }

        button.btn.btn-light.year-dropdown-button:hover,
        button.btn.btn-light.year-dropdown-button:focus,
        button.btn.btn-light.year-dropdown-button:active,
        button.btn.btn-light.year-dropdown-button.active,
        button.btn.btn-light.year-dropdown-button.show {
          background: transparent !important;
          border: none !important;
          box-shadow: none !important;
          opacity: 0.8 !important;
          color: #1a202c !important;
        }

        .year-dropdown-button .year-display {
          font-size: 3rem !important;
          font-weight: 600 !important;
          margin-right: auto !important;
        }

        .year-dropdown-button .year-dropdown-arrow {
          width: 28px !important;
          height: 28px !important;
          transition: transform 0.2s ease !important;
          color: #6c757d !important;
        }

        .year-dropdown-button .year-dropdown-arrow.open {
          transform: rotate(180deg) !important;
        }

        div.dropdown-menu.year-dropdown-menu {
          min-width: 200px !important;
          max-height: 300px !important;
          overflow-y: auto !important;
          padding: 0.5rem 0 !important;
          margin-top: 0.5rem !important;
          border: 1px solid rgba(0, 0, 0, 0.1) !important;
          border-radius: 8px !important;
          box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
          background: white !important;
        }

        button.dropdown-item.year-dropdown-item {
          padding: 1rem 1.5rem !important;
          font-size: 2rem !important;
          color: #495057 !important;
          text-align: center !important;
          transition: all 0.15s ease !important;
          background: transparent !important;
          cursor: pointer !important;
          font-weight: 500 !important;
          white-space: nowrap !important;
        }

        button.dropdown-item.year-dropdown-item:hover,
        button.dropdown-item.year-dropdown-item:focus,
        button.dropdown-item.year-dropdown-item:active {
          background-color: #f8f9fa !important;
          color: #1a202c !important;
        }

        button.dropdown-item.year-dropdown-item.active {
          background-color: #e9ecef !important;
          color: #2b6cb0 !important;
          font-weight: 600 !important;
        }

        /* Custom scrollbar */
        .year-dropdown-menu::-webkit-scrollbar {
          width: 8px !important;
        }

        .year-dropdown-menu::-webkit-scrollbar-track {
          background: #f1f5f9 !important;
          border-radius: 4px !important;
        }

        .year-dropdown-menu::-webkit-scrollbar-thumb {
          background: #cbd5e1 !important;
          border-radius: 4px !important;
        }

        .year-dropdown-menu::-webkit-scrollbar-thumb:hover {
          background: #94a3b8 !important;
        }

        .modern-card {
          border-radius: 16px !important;
          box-shadow: 0 2px 12px rgba(0,0,0,0.06) !important;
          background: #fff !important;
          transition: box-shadow 0.2s, transform 0.2s;
        }
        .modern-card:hover {
          box-shadow: 0 4px 24px #1976d233 !important;
          transform: translateY(-2px) scale(1.01);
        }
        .modern-section-header-icon {
          font-size: 1.3em;
          margin-left: 8px;
        }
        .modern-add-btn {
          border-radius: 999px !important;
          font-weight: 700 !important;
          font-size: 1.2rem !important;
          padding: 16px 40px !important;
          box-shadow: 0 2px 8px rgba(0,180,80,0.10) !important;
        }
        .modern-form-input, .modern-form-input:focus {
          border-radius: 10px !important;
          box-shadow: 0 1px 4px rgba(0,0,0,0.04) !important;
          border: 1px solid #e0e0e0 !important;
          background: #fff !important;
        }
        .modern-month-card {
          border-radius: 18px !important;
          box-shadow: 0 2px 12px rgba(0,0,0,0.06) !important;
          background: #fff !important;
          margin-bottom: 32px;
          padding: 24px 20px 12px 20px;
          transition: box-shadow 0.2s, transform 0.2s;
        }
        .modern-week-card {
          border-radius: 14px !important;
          box-shadow: 0 1px 6px rgba(0,0,0,0.04) !important;
          background: #f8fafc !important;
          margin-bottom: 16px;
          padding: 16px 14px 10px 14px;
          transition: box-shadow 0.2s, transform 0.2s;
        }
        .modern-week-card.editing {
          background: #e3e9f7 !important;
        }
        .modern-edit-btn {
          border-radius: 999px !important;
          font-weight: 700 !important;
          font-size: 1rem !important;
          padding: 8px 18px !important;
          box-shadow: 0 2px 8px rgba(0,102,204,0.10) !important;
          background: #0066CC !important;
          color: #fff !important;
          border: none !important;
          margin-right: 8px;
          margin-top: 4px;
          transition: background 0.2s, box-shadow 0.2s;
        }
        .modern-edit-btn:hover {
          background: #0052a3 !important;
          box-shadow: 0 4px 16px #0066cc33 !important;
        }
        .modern-title {
          font-weight: 800;
          font-size: 2.5rem;
          letter-spacing: 1px;
          margin-bottom: 24px;
          text-align: center;
        }
        .modern-month-name {
          font-size: 2rem;
          font-weight: 700;
          color: #1976d2;
          margin-bottom: 8px;
        }
        .modern-month-description {
          font-size: 1.1rem;
          color: #555;
          margin-bottom: 8px;
        }
        .modern-week-title {
          font-size: 1.2rem;
          font-weight: 600;
          color: #333;
        }
        .modern-week-description {
          font-size: 1rem;
          color: #666;
        }
        .modern-empty-message {
          color: #aaa;
          font-size: 1rem;
          font-style: italic;
        }
        .modern-weeks-container {
          margin-top: 16px;
        }
        .modern-year-dropdown {
          margin-bottom: 32px;
        }
        .modern-months-grid {
          display: flex;
          flex-direction: column;
          gap: 32px;
          width: 100%;
        }
      `}</style>
      <div style={containerStyle}>
        <button
          style={tourButtonStyle}
          onClick={handleTourButtonClick}
          title="הפעל סיור מודרך"
          className="training-guide-button"
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "scale(1.1)";
            e.currentTarget.style.boxShadow = "0 4px 8px rgba(0,0,0,0.3)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "scale(1)";
            e.currentTarget.style.boxShadow = "0 2px 4px rgba(0,0,0,0.2)";
          }}
        >
          <FaQuestionCircle size={20} />
        </button>
        <p className="modern-title secular-one-regular title">
          תכנית אימונים שנתית
        </p>
        <div className="modern-year-dropdown">
          <YearDropdown year={selectedYear} />
        </div>
        <div className="modern-months-grid">
          {renderMonths(selectedYear, professionalContent?.years?.["0"]?.[selectedYear])}
        </div>
      </div>
      <Joyride
        steps={steps}
        run={runTour}
        continuous
        showProgress
        showSkipButton
        disableScrolling={true}
        callback={handleJoyrideCallback}
        styles={{
          options: {
            arrowColor: "#fff",
            backgroundColor: "#fff",
            overlayColor: "rgba(0, 0, 0, 0.5)",
            primaryColor: "#0066CC",
            textColor: "#333",
            zIndex: 1000,
          },
          tooltipContainer: {
            direction: "rtl",
            textAlign: "right",
          },
          buttonNext: {
            backgroundColor: "#0066CC",
            fontSize: 14,
            padding: "8px 16px",
          },
          buttonBack: {
            marginRight: 10,
            fontSize: 14,
            padding: "8px 16px",
          },
          buttonSkip: {
            color: "#666",
            fontSize: 14,
            padding: "8px 16px",
          },
        }}
        locale={{
          back: "הקודם",
          close: "סגור",
          last: "סיום",
          next: "הבא",
          skip: "דלג",
        }}
      />
    </div>
  );
}


export default TrainingPlanPane;
